/**
 * Phase 35 Session 35.2a — useTooltip hook.
 *
 * Returns `{data, isLoading, error, isUnavailable}` for a single
 * tooltip body keyed by `helpKey`. The most-mounted hook in the
 * SDK — every `<Tooltip helpKey="X">` primitive that 35.2b
 * promotes to `@heroui/react` calls this under the hood.
 *
 * Resolution flow:
 *   1. If `disableNetwork` is on (storefront pre-auth, offline
 *      mode), skip the fetch and look up the in-repo fallback
 *      synchronously. Returns the fallback or `isUnavailable=true`.
 *   2. Otherwise issue `GET /help/tooltips/:helpKey`. On 200, hand
 *      back the response. On 404, the resolver returned null —
 *      either the caller isn't entitled OR the helpKey doesn't
 *      exist. The opaque-404 contract from 35.1a means we can't
 *      distinguish; the SDK surfaces both as `isUnavailable=true`
 *      and the consumer (Tooltip primitive in 35.2b) renders
 *      nothing. This is the gating-via-invisibility behavior the
 *      Phase 35 spec calls out.
 *   3. On any other error (5xx, network failure), fall through to
 *      the in-repo fallback. Disaster-recovery floor — a CMS
 *      outage doesn't blank every tooltip in the app.
 *
 * The hook deliberately doesn't expose the gating distinction.
 * Component code that branches on "is this hidden because the user
 * isn't entitled?" vs. "is this hidden because the helpKey is
 * missing?" creates surface for permission-leak bugs. The Tooltip
 * primitive renders nothing in either case + the merchant never
 * sees a `?` icon for help they can't access.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { HelpTooltipResponse } from '@commerce-os/shared-types';
import { helpApi } from '@commerce-os/api-client';
import { getHelpTransport, isHelpTransportConfigured } from '../client';
import {
  helpKeys,
  useHelpClientStore,
  useHelpLocale,
} from '../store/helpClientStore';
import { resolveTooltipFallback } from '../fallbacks/fallbacks';

export interface UseTooltipOptions {
  /** Override the active locale for this lookup. Defaults to the
   *  store's locale (which itself defaults to en-US). */
  locale?: string;
  /** Set to `false` to deliberately skip the fetch — useful when
   *  the consumer wants to check entitlement without showing the
   *  body. Disabled queries return `isLoading=false` + `data=null`. */
  enabled?: boolean;
  /** Override stale time (ms). Defaults to 5 min, matching the
   *  backend Redis cache lower bound from 35.1b's HelpCacheService. */
  staleTime?: number;
}

export interface TooltipQueryResult {
  /** The tooltip body when the resolver returned one (or a
   *  fallback when the network failed). Null when unavailable. */
  data: HelpTooltipResponse | null;
  isLoading: boolean;
  isError: boolean;
  /** True when the resolver returned null AND no fallback exists.
   *  The Tooltip primitive renders nothing in this case. */
  isUnavailable: boolean;
  /** Underlying React Query result for advanced consumers. */
  query: UseQueryResult<HelpTooltipResponse | null, Error>;
}

const DEFAULT_STALE_TIME_MS = 5 * 60 * 1000; // 5 min

export function useTooltip(
  helpKey: string,
  options: UseTooltipOptions = {},
): TooltipQueryResult {
  const storeLocale = useHelpLocale();
  const locale = options.locale ?? storeLocale;
  const disableNetwork = useHelpClientStore((s) => s.disableNetwork);
  const staleAtMs = useHelpClientStore((s) => s.staleAtMs);
  const trimmed = typeof helpKey === 'string' ? helpKey.trim() : '';
  const enabled = (options.enabled ?? true) && trimmed.length > 0;

  const query = useQuery<HelpTooltipResponse | null, Error>({
    queryKey: [...helpKeys.tooltip(trimmed, locale), staleAtMs],
    queryFn: async () => {
      // Network-disabled mode: synchronous fallback only. Storefront
      // pre-auth + pos.offline_mode flows land here.
      if (disableNetwork) {
        return tooltipFromFallback(trimmed, locale);
      }
      // Transport not registered yet — surface the fallback so app
      // boot doesn't show empty `?` icons during the first render
      // tick before setHelpTransport fires.
      if (!isHelpTransportConfigured()) {
        return tooltipFromFallback(trimmed, locale);
      }
      try {
        const result = await helpApi.getTooltip(getHelpTransport(), trimmed);
        if (result) return result;
        // Resolver returned null (caller not entitled OR helpKey
        // doesn't exist — opaque-404 contract). NO fallback because
        // gating is intentional.
        return null;
      } catch {
        // Network failure / 5xx. Fall through to the disaster-recovery
        // fallback so the UI stays usable.
        return tooltipFromFallback(trimmed, locale);
      }
    },
    enabled,
    staleTime: options.staleTime ?? DEFAULT_STALE_TIME_MS,
    retry: 1,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    isUnavailable: enabled && !query.isLoading && !query.data,
    query,
  };
}

function tooltipFromFallback(
  helpKey: string,
  locale: string,
): HelpTooltipResponse | null {
  const fallback = resolveTooltipFallback(helpKey, locale);
  if (!fallback) return null;
  return {
    helpKey: fallback.helpKey,
    body: fallback.body,
    linkToArticleId: fallback.linkToArticleId ?? null,
  };
}
